// --------------------
// Types
// --------------------

import { ECurrency } from "../../../invoice/create/interfaces/invoice.interfaces";

export type WorkforceInput = {
  employees?: Array<{ uid: string }>;
  contractors?: Array<{ uid: string }>;
  supervisors?: Array<{ uid: string }>;
};

export type EventSchedulerInput = {
  eventPreferences?: {
    eventConfig?: any;
  };
};

export type ScopeItem = {
  id?: string;
  service?: string;
  serviceUid?: string;
  price?: string | number;
  units?: string | number;
  measure?: string;
  notes?: string;
  alias?: string;
  total?: number;
  mode?: string;
  frequencyType?: string;
};

export type CreatedJobWithoutQuoteInput = {
  workforce?: WorkforceInput;
  eventScheduler?: EventSchedulerInput;
  scopeOfWork?: ScopeItem[] | null;
  client?: { uid: string; coverageArea?: { id: number } };
  companyId?: number;
  alias?: string;
};

export type ScopeOfWork = {
  name: string;
  typeOfServiceUid: string;
  averagePeople: number;
  squareFootage: string;
  typeOfService: {
    costBias: number;
    baseRatePerHour: number;
  };
  frequencyType: string;

  clientUid: string | null;
  coverageAreaId: number | null;
  companyId: number | null;
  costOverride?: Record<string, any>;
  meta?: Record<string, any>;
};

export type FormattedForCreatedJobWithOutQuoteOutput = {
  workforce: Array<{ uid: string; role: "employee" | "contractor" | "supervisor" }>;
  eventConfig: any | null;
  scopeOfWork: ScopeOfWork;
  alias: string;
  type: string;
};

// --------------------
// Helpers
// --------------------

export const buildScopeOfWork = (
  item: ScopeItem,
  clientUid: string | null,
  coverageAreaId: number | null,
  companyId: number | null,
  frequencyType?: string,
): ScopeOfWork => {
  return {
    name: item.alias?.trim() || item.service?.trim() || "Unknown Service",
    typeOfServiceUid: item.serviceUid ?? "MOCK_SERVICE_UID",
    averagePeople: Number(item.units) || 1,
    squareFootage: "0-1000",
    typeOfService: {
      costBias: 1,
      baseRatePerHour: typeof item.price === "number" ? item.price : Number(item.price) || 0,
    },
    frequencyType: frequencyType ?? item?.frequencyType ?? "weekly",
    meta: {
      notes: item.notes,
    },
    costOverride: {
      by: item.measure,
      value: item.price,
      units: item.units,
      currency: ECurrency.USD,
    },
    clientUid,
    coverageAreaId,
    companyId,
  };
};

// --------------------
// Main formatter
// --------------------

export const formatedForCreatedJobWithOutQuote = (
  data: CreatedJobWithoutQuoteInput,
  companyId: number,
  eventBase: any,
  type: string,
): FormattedForCreatedJobWithOutQuoteOutput => {
  const w = data?.workforce ?? {};

  const workforce: FormattedForCreatedJobWithOutQuoteOutput["workforce"] = [
    ...(w.employees ?? [])?.map((p) => ({ uid: p.uid, role: "employee" as const })),
    ...(w.contractors ?? [])?.map((p) => ({ uid: p.uid, role: "contractor" as const })),
    ...(w.supervisors ?? [])?.map((p) => ({ uid: p.uid, role: "supervisor" as const })),
  ].filter((p) => !!p.uid);

  const scopeItem: ScopeItem = Array.isArray(data?.scopeOfWork) && data.scopeOfWork.length > 0 ? data.scopeOfWork[0] : {};

  const scopeOfWork = buildScopeOfWork(
    scopeItem,
    data?.client?.uid ?? null,
    data?.client?.coverageArea?.id ?? null,
    companyId,
    (data as any)?.frequencyType,
  );

  return {
    workforce,
    eventConfig: eventBase,
    scopeOfWork,
    alias: data?.alias ?? "",
    type,
  };
};
